/**
 * Lighter Client - Browser-based Lighter SDK wrapper
 * Mirrors BrowserHyperliquidClient structure for Lighter Protocol
 * Initialized with credentials from LighterCredentialsManager
 */

class LighterClient {
    constructor() {
        this.sdk = null;
        this.isInitialized = false;
        this.privateKey = null;
        this.publicAddress = null;
        this.accountIndex = null;
        this.apiKeyIndex = null;
        this.network = 'mainnet';
    }

    /**
     * Initialize the Lighter SDK with credentials
     * @param {string} privateKey - Private key / API key
     * @param {string} publicAddress - Public address
     * @param {number} accountIndex - Account index (default 0)
     * @param {number} apiKeyIndex - API key index (default 0)
     * @param {string} network - Network (mainnet/testnet)
     */
    async initialize(privateKey, publicAddress, accountIndex = 0, apiKeyIndex = 0, network = 'mainnet') {
        try {
            this.privateKey = privateKey;
            this.publicAddress = publicAddress;
            this.accountIndex = accountIndex;
            this.apiKeyIndex = apiKeyIndex;
            this.network = network;

            // Wait for WASM to load first
            if (window.loadLighterWASM && !window.LighterWASM?.ready) {
                await window.loadLighterWASM();
            }

            // Dynamic import of LighterSDK
            const { LighterSDK } = await import('/lightersdk/sdk/core/lighter-sdk.js');

            // Initialize SDK
            this.sdk = new LighterSDK({
                privateKey: this.privateKey,
                accountIndex: this.accountIndex,
                apiKeyIndex: this.apiKeyIndex,
                network: this.network
            });

            await this.sdk.init();

            this.isInitialized = true;
            // console.log('✅ [LIGHTER-CLIENT] Initialized successfully');
            return true;

        } catch (error) {
            console.error('❌ [LIGHTER-CLIENT] Initialization failed:', error);
            this.isInitialized = false;
            throw error;
        }
    }

    /**
     * Re-initialize with current credentials from LighterCredentialsManager
     */
    async reinitialize() {
        const creds = window.LighterCredentialsManager?.getLighterCredentials();
        if (!creds || !creds.apiKey) {
            throw new Error('No Lighter credentials found');
        }

        return await this.initialize(
            creds.apiKey,
            creds.publicAddress,
            creds.accountIndex,
            creds.apiKeyIndex,
            creds.network
        );
    }

    /**
     * Get available markets
     */
    async getMarkets() {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.getMarkets();
    }

    /**
     * Get account info (balance, positions, etc.)
     */
    async getAccountInfo() {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.getAccountInfo();
    }

    /**
     * Get user positions
     */
    async getPositions() {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.getPositions();
    }

    /**
     * Get open orders
     */
    async getOpenOrders() {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.getOpenOrders();
    }

    /**
     * Get balance
     */
    async getBalance() {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.getBalance();
    }

    /**
     * Place an order
     * @param {Object} orderParams - Order parameters
     */
    async placeOrder(orderParams) {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.placeOrder(orderParams);
    }

    /**
     * Cancel an order
     * @param {string} orderId - Order ID to cancel
     */
    async cancelOrder(orderId) {
        if (!this.isInitialized) {
            throw new Error('Lighter client not initialized');
        }
        return await this.sdk.cancelOrder(orderId);
    }

    /**
     * Clear credentials from memory
     */
    clearCredentials() {
        this.sdk = null;
        this.isInitialized = false;
        this.privateKey = null;
        this.publicAddress = null;
        this.accountIndex = null;
        this.apiKeyIndex = null;
        this.network = 'mainnet';
        console.log('✅ [LIGHTER-CLIENT] Credentials cleared');
    }

    /**
     * Check if client is ready
     */
    isReady() {
        return this.isInitialized && this.sdk !== null;
    }

    /**
     * Get current credentials
     */
    getCredentials() {
        return {
            publicAddress: this.publicAddress,
            accountIndex: this.accountIndex,
            apiKeyIndex: this.apiKeyIndex,
            network: this.network
        };
    }
}

// Create singleton instance
const lighterClient = new LighterClient();
window.LighterClient = lighterClient;

export { LighterClient, lighterClient as default };
